// NGOFormFields.tsx
import type { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { Controller } from "react-hook-form";
import { Input, MultiSelect, Select, Textarea } from "../../components";
import FileUploadField from "../../components/fileUploadInput/FileUploadInput";
import { uploadNgoImage_V2 } from "../../Services_V2/NGO.service";
import styles from "../AddEditNGOPage/AddEditNGOPage.module.css";
import type { INGOForm } from "./const";
import { BANK_ACCOUNT_TYPE_OPTIONS, NgoInputs } from "./const";

interface NGOFormFieldsProps {
  control: Control<INGOForm>;
  register: UseFormRegister<INGOForm>;
  errors: FieldErrors<INGOForm>;
  categoryOptions: { label: string; value: string }[];
  cityOptions: { label: string; value: string }[];
  isEdit: boolean;
  logoPreview: string | null;
  UpiQrPreview: string | null;
  cert80GPreview: string | null;
  trustDeedPreview: string | null;
  setLogoPreview: (url: string | null) => void;
  setUpiQrPreview: (url: string | null) => void;
  setCert80GPreview: (url: string | null) => void;
  setTrustDeedPreview: (url: string | null) => void;
  resolvedNgoId?: string;
}

export default function NGOFormFields({
  control,
  register,
  errors,
  categoryOptions,
  cityOptions,
  isEdit,
  logoPreview,
  cert80GPreview,
  trustDeedPreview,
  UpiQrPreview,
  setLogoPreview,
  setCert80GPreview,
  setTrustDeedPreview,
  setUpiQrPreview,
  resolvedNgoId,
}: NGOFormFieldsProps) {
  return (
    <div className={styles.grid}>
      {/* NGO Name */}
      <Input
        label="NGO Name *"
        placeholder="Enter NGO name"
        fullWidth
        {...register(NgoInputs.NAME)}
        error={!!errors[NgoInputs.NAME]}
        helperText={errors[NgoInputs.NAME]?.message}
      />

      {/* Category */}
      <Controller
        name={NgoInputs.CATEGORY}
        control={control}
        render={({ field }) => (
          <Select
            label="NGO Category *"
            placeholder="Select category"
            options={categoryOptions}
            fullWidth
            {...field}
            error={!!errors[NgoInputs.CATEGORY]}
            helperText={errors[NgoInputs.CATEGORY]?.message}
          />
        )}
      />

      {/* Tagline */}
      <Textarea
        rows={2}
        label="Tagline *"
        placeholder="Short tagline"
        fullWidth
        {...register(NgoInputs.TAGLINE)}
        error={!!errors[NgoInputs.TAGLINE]}
        helperText={errors[NgoInputs.TAGLINE]?.message}
      />

      {/* Cities */}
      <Controller
        name={NgoInputs.CITIES}
        control={control}
        render={({ field }) => (
          <MultiSelect
            label="Cities *"
            options={cityOptions}
            value={cityOptions.filter((o) => field.value?.includes(o.value))}
            onValueChange={(selected) =>
              field.onChange(selected.map((o) => o.value))
            }
            error={!!errors[NgoInputs.CITIES]}
            helperText={errors[NgoInputs.CITIES]?.message}
          />
        )}
      />

      {/* Summary */}
      <Textarea
        rows={3}
        label="Summary *"
        placeholder="Short description"
        fullWidth
        {...register(NgoInputs.SUMMARY)}
        error={!!errors[NgoInputs.SUMMARY]}
        helperText={errors[NgoInputs.SUMMARY]?.message}
      />

      {/* About */}
      <Textarea
        rows={5}
        label="About NGO *"
        placeholder="Detailed about section"
        fullWidth
        {...register(NgoInputs.ABOUT)}
        error={!!errors[NgoInputs.ABOUT]}
        helperText={errors[NgoInputs.ABOUT]?.message}
      />

      {/* Darpan ID */}
      <Input
        label="Darpan ID *"
        placeholder="Enter Darpan ID"
        fullWidth
        {...register(NgoInputs.DARPAN_ID)}
        error={!!errors[NgoInputs.DARPAN_ID]}
        helperText={errors[NgoInputs.DARPAN_ID]?.message}
      />

      {/* Email */}
      <Input
        label="NGO Official Email"
        placeholder="info@yourngo.org"
        fullWidth
        {...register("email")}
        error={!!errors.email}
        helperText={errors.email?.message}
      />

      {/* Website */}
      <Input
        label="Website URL"
        placeholder="https://example.com"
        fullWidth
        {...register(NgoInputs.WEBSITE)}
        error={!!errors[NgoInputs.WEBSITE]}
        helperText={errors[NgoInputs.WEBSITE]?.message}
      />
      <div className={styles.sectionTitle}>
        Bank Account Details (for Donations)
      </div>

      <div className={styles.grid}>
        <Input
          label="Bank Name *"
          placeholder="State Bank of India"
          fullWidth
          {...register(NgoInputs.BANK_NAME)}
          error={!!errors.bankDetails?.bankName}
          helperText={errors.bankDetails?.bankName?.message}
        />

        <Input
          label="Bank Branch *"
          placeholder="Main Branch"
          fullWidth
          {...register(NgoInputs.BANK_BRANCH)}
          error={!!errors.bankDetails?.branch}
          helperText={errors.bankDetails?.branch?.message}
        />

        <Input
          label="IFSC Code *"
          placeholder="SBIN0001234"
          fullWidth
          {...register(NgoInputs.IFSC_CODE)}
          error={!!errors.bankDetails?.ifscCode}
          helperText={errors.bankDetails?.ifscCode?.message}
        />

        <Controller
          name={NgoInputs.ACCOUNT_TYPE}
          control={control}
          render={({ field }) => (
            <Select
              label="Account Type *"
              placeholder="Select account type"
              options={BANK_ACCOUNT_TYPE_OPTIONS}
              fullWidth
              {...field}
              value={field.value ?? ""}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.bankDetails?.accountType}
              helperText={errors.bankDetails?.accountType?.message}
            />
          )}
        />

        <Input
          label="Beneficiary/Account Name *"
          placeholder="Name as in bank account"
          fullWidth
          {...register(NgoInputs.BENEFICIARY_NAME)}
          error={!!errors.bankDetails?.beneficiaryName}
          helperText={errors.bankDetails?.beneficiaryName?.message}
        />

        <Input
          label="Account Number *"
          placeholder="Enter account number"
          fullWidth
          {...register(NgoInputs.ACCOUNT_NUMBER)}
          error={!!errors.bankDetails?.accountNumber}
          helperText={errors.bankDetails?.accountNumber?.message}
        />

        <Input
          label="UPI ID (Optional)"
          placeholder="ngo.name@upi"
          fullWidth
          {...register(NgoInputs.UPI_ID)}
          error={!!errors.bankDetails?.upiId}
          helperText={errors.bankDetails?.upiId?.message}
        />
      </div>

      {/* File Uploads - always shown */}
      <FileUploadField
        name={NgoInputs.DONATION_QR}
        control={control}
        errors={errors}
        label={isEdit ? "Upload UPI QR" : "Upload UPI QR *"}
        fieldabel="UPI QR"
        subText="Click to upload image (JPG, PNG)"
        accept="image/*"
        previewUrl={UpiQrPreview}
        onPreviewChange={setUpiQrPreview}
        entityId={resolvedNgoId}
        onUpload={uploadNgoImage_V2}
        fieldKey="donationQr"
        isEdit={isEdit}
      />

      <FileUploadField
        name={NgoInputs.NGO_IMAGE}
        control={control}
        errors={errors}
        label={isEdit ? "Upload Logo" : "Upload Logo *"}
        fieldabel="NGO Logo"
        subText="Click to upload image (JPG, PNG)"
        accept="image/*"
        previewUrl={logoPreview}
        onPreviewChange={setLogoPreview}
        entityId={resolvedNgoId}
        onUpload={uploadNgoImage_V2}
        fieldKey="logo"
        isEdit={isEdit}
      />

      <FileUploadField
        name={NgoInputs.EIGHTY_G_CERTIFICATE}
        control={control}
        errors={errors}
        label={isEdit ? "Upload 80G Certificate" : "Upload 80G Certificate *"}
        fieldabel="80G Certificate"
        subText="Click to upload PDF or Image"
        accept="image/*,application/pdf"
        previewUrl={cert80GPreview}
        onPreviewChange={setCert80GPreview}
        entityId={resolvedNgoId}
        onUpload={uploadNgoImage_V2}
        fieldKey="eightyGCertificate"
        isEdit={isEdit}
      />

      <FileUploadField
        name={NgoInputs.TRUST_DEED_CERTIFICATE}
        control={control}
        errors={errors}
        label={isEdit ? "Upload Trust Deed" : "Upload Trust Deed *"}
        fieldabel="Trust Deed Certificate"
        subText="Click to upload PDF or Image"
        accept="image/*,application/pdf"
        previewUrl={trustDeedPreview}
        onPreviewChange={setTrustDeedPreview}
        entityId={resolvedNgoId}
        onUpload={uploadNgoImage_V2}
        fieldKey="trustDeedCertificate"
        isEdit={isEdit}
      />

      {/* Contact person fields - only in create mode */}
      {!isEdit && (
        <>
          <Input
            label="Contact Person Name *"
            placeholder="John Doe"
            fullWidth
            {...register("contact_person.name")}
            error={!!errors.contact_person?.name}
            helperText={errors.contact_person?.name?.message}
          />

          <Input
            label="Contact Person Email *"
            placeholder="john@ngo.org"
            fullWidth
            {...register("contact_person.email")}
            error={!!errors.contact_person?.email}
            helperText={errors.contact_person?.email?.message}
          />

          <Input
            label="Contact Person Mobile"
            placeholder="9876543210"
            fullWidth
            {...register("contact_person.number")}
            error={!!errors.contact_person?.number}
            helperText={errors.contact_person?.number?.message}
          />
        </>
      )}
    </div>
  );
}
